import { computed, ref, type ComputedRef, type Ref } from "vue";
import { isJust, type Maybe } from "./maybe";

const busyRoots = ref<Set<string>>(new Set());
const idleListeners = new Set<(root: string) => void>();

export function beginCloudActivity(root: string): void {
  const next = new Set(busyRoots.value);
  next.add(root);
  busyRoots.value = next;
}

export function endCloudActivity(root: string): void {
  const next = new Set(busyRoots.value);
  next.delete(root);
  busyRoots.value = next;
  for (const listener of idleListeners) listener(root);
}

export async function withCloudActivity<T>(
  root: string,
  fn: () => Promise<T>,
): Promise<T> {
  beginCloudActivity(root);
  try {
    return await fn();
  } finally {
    endCloudActivity(root);
  }
}

/** True when upload/pull sync is in flight for `root`. */
export function isCloudActivityBusy(root: string): boolean {
  return busyRoots.value.has(root);
}

/** Run `listener` when in-flight cloud work for a root finishes. */
export function onCloudActivityIdle(
  listener: (root: string) => void,
): () => void {
  idleListeners.add(listener);
  return () => {
    idleListeners.delete(listener);
  };
}

/** Reactive: true when `root` has in-flight cloud sync. */
export function useCloudActivityBusy(
  root: Ref<Maybe<string> | string | null>,
): ComputedRef<boolean> {
  return computed(() => {
    const raw = root.value;
    const path =
      raw === null
        ? null
        : typeof raw === "string"
          ? raw
          : isJust(raw)
            ? raw.value
            : null;
    return path !== null && path.length > 0 && busyRoots.value.has(path);
  });
}
